import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

interface TourCreationProgressProps {
  onComplete: () => void;
  isRetrying?: boolean;
}

const TourCreationProgress: React.FC<TourCreationProgressProps> = ({ onComplete, isRetrying = false }) => {
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('Creating your tour...');
  const [icon, setIcon] = useState('🗺️');

  useEffect(() => {
    // Reset progress at start or when retrying
    setProgress(0);

    // 2% per second for 50s, 5% per second for 20s (retry)
    const interval = 1000; // 1s
    const increment = isRetrying ? 5 : 2;

    let currentProgress = 0;

    const timer = setInterval(() => {
      currentProgress += increment;

      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
        setTimeout(() => {
          console.log('🎯 Progress complete, calling onComplete...');
          onComplete();
        }, 300);
      }

      setProgress(currentProgress);

      if (isRetrying) {
        setMessage('Almost ready, final check...');
        setIcon('⏳');
      } else {
        if (currentProgress < 15) {
          setMessage('Creating your tour...');
          setIcon('🗺️');
        } else if (currentProgress < 35) {
          setMessage('Finding best places...');
          setIcon('📍');
        } else if (currentProgress < 75) {
          setMessage('Generating audio podcasts...');
          setIcon('🎙️');
        } else if (currentProgress < 95) {
          setMessage('Finalizing tour...');
          setIcon('✨');
        } else {
          setMessage('Loading your tour...');
          setIcon('🎉');
        }
      }
    }, interval);

    return () => clearInterval(timer);
  }, [onComplete, isRetrying]);

  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6">
      <div className="relative w-32 h-32">
        <svg className="transform -rotate-90 w-32 h-32">
          <circle
            cx="64"
            cy="64"
            r="54"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-gray-200"
          />
          <circle
            cx="64"
            cy="64"
            r="54"
            stroke="url(#gradient)"
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-300 ease-out"
            strokeLinecap="round"
          />
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a855f7" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
        </svg>
        
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl mb-1">{icon}</span>
          <span className="text-lg font-bold text-gray-900">{Math.round(progress)}%</span>
        </div>
      </div>
      
      <div className="w-full space-y-3">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <p className="text-sm font-medium text-gray-700">{message}</p>
        </div>
      </div>

      <p className="text-xs text-gray-400 text-center max-w-xs">
        {isRetrying ? 
          'Checking for your tour, almost there!' : 
          'This may take up to a minute. Please don\'t close this window.'}
      </p>
    </div>
  );
};

export default TourCreationProgress;
